import {
  orchestrateLibraryRagApplication,
  type LibraryKnowledgeBundle,
  type LibraryKnowledgeGateway,
  type LibraryKnowledgePlanner,
  type LibraryRagOrchestrationRequest,
  type LibraryRagOrchestrationResult,
  type LibrarySemanticCritic,
} from '@cogo/agentic-generator/library-rag-agent-orchestration'
import {
  admitPlatformLibraryKnowledgeBundle,
  hashPlatformCanonicalJson,
  parsePlatformLibraryRagCriticReceiptV1,
  parsePlatformLibraryRagPlanReceiptV1,
  PLATFORM_LIBRARY_KNOWLEDGE_BUNDLE_PATH,
  PLATFORM_LIBRARY_RAG_CRITIC_PATH,
  PLATFORM_LIBRARY_RAG_PLAN_PATH,
  type PlatformLibraryKnowledgeBundleV1,
  type PlatformLibraryLocale,
  type PlatformLibraryRagPlanReceiptV1,
  type PlatformLibraryRagRequestV1,
} from '@cogo/agentic-generator/platform-library-rag'
import { getStudioAccessToken } from '@/services/auth-provider'
import {
  cogoPlatformApiUrl,
  withCogoDevProxyHeaders,
} from '@/services/cogo-platform-config'
import { admitAuthenticatedStudioLibraryRagResolution } from './studio-library-rag-runtime-authority'

const PLATFORM_REQUEST_TIMEOUT_MS = 38_000
const PLATFORM_RESPONSE_MAX_BYTES = 1_250_000

interface ActivePlatformOrchestration {
  request: PlatformLibraryRagRequestV1
  compact: PlatformLibraryKnowledgeBundleV1
  plan?: PlatformLibraryRagPlanReceiptV1
}

/**
 * One request-instance coordinator joins the platform's three closed phases:
 * admitted compact bundle -> Qwen plan receipt -> local canonical compiler ->
 * Gemma critic receipt. State never crosses applicationInstanceId boundaries.
 */
export function createStudioLibraryRagPlatformCoordinator(): {
  orchestrate(
    request: LibraryRagOrchestrationRequest,
  ): Promise<LibraryRagOrchestrationResult>
} {
  const active = new Map<string, ActivePlatformOrchestration>()

  const gateway: LibraryKnowledgeGateway = {
    async retrieve(request) {
      const wireRequest = platformRequestFrom(request)
      active.delete(wireRequest.applicationInstanceId)
      const response = await postAuthenticatedPlatformJson(
        PLATFORM_LIBRARY_KNOWLEDGE_BUNDLE_PATH,
        wireRequest,
      )
      const admitted = await admitPlatformLibraryKnowledgeBundle(
        response,
        wireRequest,
      )
      active.set(wireRequest.applicationInstanceId, {
        request: structuredClone(wireRequest),
        compact: structuredClone(admitted.compact),
      })
      return admitted.rich as LibraryKnowledgeBundle
    },
  }

  const planner: LibraryKnowledgePlanner = {
    identity: {
      role: 'knowledge-planner',
      provider: 'platform-vllm',
      model: 'platform-signed-plan-receipt',
    },
    async select({ request }) {
      const state = requireActive(active, request)
      const response = await postAuthenticatedPlatformJson(
        PLATFORM_LIBRARY_RAG_PLAN_PATH,
        state.request,
      )
      const plan = await parsePlatformLibraryRagPlanReceiptV1(response)
      if (!(await planBindsActiveState(plan, state))) {
        active.delete(request.applicationInstanceId)
        throw new Error('platform-library-plan-binding-mismatch')
      }
      state.plan = plan
      return {
        selection: structuredClone(plan.selection),
        authorityReceipt: plan,
      }
    },
  }

  const critic: LibrarySemanticCritic = {
    identity: {
      role: 'semantic-critic',
      provider: 'platform-vllm',
      model: 'platform-bound-critic-receipt',
    },
    async review({ request, selection, artifact }) {
      const state = requireActive(active, request)
      const plan = state.plan
      if (!plan) {
        active.delete(request.applicationInstanceId)
        throw new Error('platform-library-plan-missing')
      }
      const compiledArtifact = structuredClone(artifact) as unknown as Record<
        string,
        unknown
      >
      const compiledArtifactContentHash =
        await hashPlatformCanonicalJson(compiledArtifact)
      try {
        const response = await postAuthenticatedPlatformJson(
          PLATFORM_LIBRARY_RAG_CRITIC_PATH,
          {
            request: state.request,
            plan,
            compiledArtifact,
            compiledArtifactContentHash,
          },
        )
        const receipt = await parsePlatformLibraryRagCriticReceiptV1(response)
        if (
          receipt.planReceiptContentHash !== plan.contentHash ||
          receipt.bundleContentHash !== state.compact.contentHash ||
          receipt.requestHash !== state.compact.requestHash ||
          receipt.scopeHash !== plan.scopeHash ||
          receipt.compiledArtifactContentHash !==
            compiledArtifactContentHash ||
          (await hashPlatformCanonicalJson(receipt.selection)) !==
            (await hashPlatformCanonicalJson(selection)) ||
          receipt.critic.verdict.traceId !==
            receipt.critic.receipt.reviewRunId ||
          !sameOrderedValues(
            receipt.critic.receipt.selectedCandidateIds,
            selectedRefsFor(receipt.selection),
          )
        ) {
          throw new Error('platform-library-critic-binding-mismatch')
        }
        return {
          verdict: structuredClone(receipt.critic.verdict),
          authorityReceipt: receipt,
        }
      } finally {
        active.delete(request.applicationInstanceId)
      }
    },
  }

  return {
    async orchestrate(request) {
      const platformRequest = {
        ...structuredClone(request),
        locale: toPlatformLibraryLocale(request.locale),
      }
      try {
        const result = await orchestrateLibraryRagApplication(platformRequest, {
          gateway,
          planner,
          critic,
        })
        return result.ok
          ? admitAuthenticatedStudioLibraryRagResolution(result)
          : result
      } finally {
        active.delete(platformRequest.applicationInstanceId)
      }
    },
  }
}

function platformRequestFrom(
  request: LibraryRagOrchestrationRequest,
): PlatformLibraryRagRequestV1 {
  return {
    prompt: request.prompt,
    target: 'web',
    locale: toPlatformLibraryLocale(request.locale),
    applicationInstanceId: request.applicationInstanceId,
  }
}

function toPlatformLibraryLocale(locale: string): PlatformLibraryLocale {
  const mapped: Record<string, PlatformLibraryLocale> = {
    en: 'en-US',
    'en-US': 'en-US',
    ko: 'ko-KR',
    'ko-KR': 'ko-KR',
    ja: 'ja-JP',
    'ja-JP': 'ja-JP',
    zh: 'zh-CN',
    'zh-CN': 'zh-CN',
    'zh-TW': 'zh-TW',
  }
  const resolved = mapped[locale]
  if (!resolved) throw new Error('platform-library-locale-unsupported')
  return resolved
}

function requireActive(
  active: Map<string, ActivePlatformOrchestration>,
  request: LibraryRagOrchestrationRequest,
): ActivePlatformOrchestration {
  const state = active.get(request.applicationInstanceId)
  if (
    !state ||
    state.request.prompt !== request.prompt ||
    state.request.target !== request.target ||
    state.request.locale !== request.locale ||
    state.request.applicationInstanceId !== request.applicationInstanceId
  ) {
    throw new Error('platform-library-orchestration-state-mismatch')
  }
  return state
}

async function planBindsActiveState(
  plan: PlatformLibraryRagPlanReceiptV1,
  state: ActivePlatformOrchestration,
): Promise<boolean> {
  const expectedSelection = {
    applicationBlueprintRef: state.compact.applicationBlueprint.ref,
    uxPatternRefs: state.compact.applicationBlueprint.uxPatternRefs,
    designOntologyReleaseRef: state.compact.designOntology.releaseRef,
  }
  return (
    plan.request.target === state.request.target &&
    plan.request.locale === state.request.locale &&
    plan.request.applicationInstanceId === state.request.applicationInstanceId &&
    plan.requestHash === state.compact.requestHash &&
    plan.bundleContentHash === state.compact.contentHash &&
    plan.selection.applicationBlueprintRef ===
      expectedSelection.applicationBlueprintRef &&
    sameOrderedValues(
      plan.selection.uxPatternRefs,
      expectedSelection.uxPatternRefs,
    ) &&
    plan.selection.designOntologyReleaseRef ===
      expectedSelection.designOntologyReleaseRef &&
    plan.selection.normalizedIntent ===
      state.compact.applicationBlueprint.userGoal &&
    plan.selection.traceId === plan.planner.receipt.reviewRunId &&
    sameOrderedValues(
      plan.planner.receipt.selectedCandidateIds,
      selectedRefsFor(plan.selection),
    ) &&
    (await hashPlatformCanonicalJson(state.request)) === plan.requestHash
  )
}

function selectedRefsFor(selection: {
  applicationBlueprintRef: string
  uxPatternRefs: readonly string[]
  designOntologyReleaseRef: string
}): string[] {
  return [
    selection.applicationBlueprintRef,
    ...selection.uxPatternRefs,
    selection.designOntologyReleaseRef,
  ]
}

async function postAuthenticatedPlatformJson(
  path: string,
  body: unknown,
): Promise<unknown> {
  const token = await getStudioAccessToken()
  if (!token) throw new Error('platform-library-authentication-required')
  const url = cogoPlatformApiUrl(path)
  const controller = new AbortController()
  const timer = setTimeout(
    () => controller.abort('platform-library-request-timeout'),
    PLATFORM_REQUEST_TIMEOUT_MS,
  )
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: withCogoDevProxyHeaders(
        {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        url,
      ),
      body: JSON.stringify(body),
      signal: controller.signal,
      credentials: 'same-origin',
    })
    if (!response.ok) {
      cancelBody(response.body, 'platform-library-response-rejected')
      throw new Error(`platform-library-response-${response.status}`)
    }
    const text = await readBoundedResponseText(response)
    return JSON.parse(text) as unknown
  } finally {
    clearTimeout(timer)
  }
}

async function readBoundedResponseText(response: Response): Promise<string> {
  const declaredLength = Number(response.headers.get('content-length'))
  if (
    Number.isFinite(declaredLength) &&
    declaredLength > PLATFORM_RESPONSE_MAX_BYTES
  ) {
    cancelBody(response.body, 'platform-library-response-too-large')
    throw new Error('platform-library-response-too-large')
  }
  if (!response.body) throw new Error('platform-library-response-empty')
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let bytes = 0
  let text = ''
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      bytes += value.byteLength
      if (bytes > PLATFORM_RESPONSE_MAX_BYTES) {
        void reader.cancel('platform-library-response-too-large').catch(() => {})
        throw new Error('platform-library-response-too-large')
      }
      text += decoder.decode(value, { stream: true })
    }
    return text + decoder.decode()
  } finally {
    reader.releaseLock()
  }
}

function cancelBody(
  body: ReadableStream<Uint8Array> | null,
  reason: string,
): void {
  if (body) void body.cancel(reason).catch(() => {})
}

function sameOrderedValues(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return (
    left.length === right.length &&
    left.every((item, index) => item === right[index])
  )
}
